import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ACCESS_REASON_LABELS,
  formatCents,
  type AdminCompany,
} from '@fsm/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Badge, Button, Card, Field, Input, Modal, Spinner, Table, Td, Th } from '../components/ui';
import { PageHeader } from '../components/ui';

/**
 * Every company on the platform.
 *
 * ## What is on this screen, and what is not
 *
 * Standing, headcount, and what that headcount comes to. Three actions: give
 * more trial, switch off, switch back on.
 *
 * There is no way from here into anybody's customers, jobs or invoices, and
 * that is a decision rather than an omission. Running a platform needs to
 * know that a company exists and whether it is paying; it does not need their
 * books. A contractor who discovers their software vendor can read their
 * customer list is a contractor who leaves, and rightly.
 *
 * ## Why the standing is shown twice
 *
 * The plan status is what the database says; the badge beside it is what the
 * company actually experiences right now, computed by the same function the
 * API gates on. They disagree constantly and usefully — `trialing` with an
 * expired date is read-only, and that is exactly the row worth looking at.
 */

const TONE: Record<string, 'slate' | 'amber' | 'blue' | 'emerald' | 'red'> = {
  subscribed: 'emerald',
  trialing: 'blue',
  trial_expired: 'amber',
  payment_failed_grace: 'amber',
  payment_failed: 'red',
  canceled: 'slate',
  suspended: 'red',
};

export function AdminPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [extending, setExtending] = useState<AdminCompany | null>(null);
  const [suspending, setSuspending] = useState<AdminCompany | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'companies'],
    queryFn: () => api.admin.companies(),
    /* Standing changes by the clock, not only by action. */
    refetchInterval: 60_000,
  });

  const refresh = () => void qc.invalidateQueries({ queryKey: ['admin'] });

  const unsuspend = useMutation({
    mutationFn: (id: string) => api.admin.unsuspend(id),
    onSuccess: refresh,
    onError: (e) => setError((e as { message?: string }).message ?? 'Could not do that'),
  });

  /*
   * No confirmation step, deliberately, unlike Suspend.
   *
   * Switching the phone off takes nothing away that cannot be put straight
   * back: the number stays bought, the call history stays, and switching it
   * on again restores exactly what was there. A dialog guarding a reversible
   * change teaches people to click through dialogs.
   */
  const telephony = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.admin.setTelephony(id, enabled),
    onSuccess: refresh,
    onError: (e) => setError((e as { message?: string }).message ?? 'Could not do that'),
  });

  /*
   * A second gate, in the browser, purely so a mistake in the menu does not
   * render an empty table and a confusing 404. The real one is on the server
   * and reads the database on every request.
   */
  if (!user?.platformOwner) {
    return (
      <div className="p-8">
        <p className="text-[14px] text-ink-3">This page does not exist.</p>
      </div>
    );
  }

  const items = data?.items ?? [];
  const paying = items.filter((c) => c.reason === 'subscribed');
  const mrr = paying.reduce((sum, c) => sum + c.monthlyCents, 0);

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <PageHeader
        eyebrow="Crewlark"
        title="Companies"
        subtitle={`${items.length} on the platform · ${paying.length} paying · ${formatCents(mrr)} a month`}
      />

      <div className="px-[30px] pb-10">
        {error && (
          <p className="mb-3 rounded-lg bg-danger-soft px-3 py-2 text-[13px] text-danger">{error}</p>
        )}

        <Card bodyClassName="p-0">
          {isLoading ? (
            <div className="grid place-items-center py-10">
              <Spinner className="size-5 text-ink-4" />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Company</Th>
                  <Th>Standing</Th>
                  <Th>Plan</Th>
                  <Th>Users</Th>
                  <Th>Monthly</Th>
                  <Th>Joined</Th>
                  <Th> </Th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id}>
                    <Td>
                      <div className="font-semibold text-ink">{c.name}</div>
                      {c.email && <div className="text-[12.5px] text-ink-4">{c.email}</div>}
                      {c.suspendedReason && (
                        <div className="mt-0.5 text-[12.5px] text-danger">
                          {c.suspendedReason}
                        </div>
                      )}
                    </Td>
                    <Td>
                      <Badge tone={TONE[c.reason] ?? 'slate'}>
                        {ACCESS_REASON_LABELS[c.reason]}
                      </Badge>
                      {c.access === 'read_only' && (
                        <div className="mt-1 text-[12px] text-ink-4">read-only</div>
                      )}
                    </Td>
                    <Td>
                      <span className="text-ink-2">{c.planStatus}</span>
                      {c.trialEndsAt && (
                        <div className="text-[12.5px] text-ink-4">
                          until {c.trialEndsAt.slice(0, 10)}
                        </div>
                      )}
                    </Td>
                    <Td>{c.seats}</Td>
                    <Td className="tnum">{formatCents(c.monthlyCents)}</Td>
                    <Td className="text-ink-3">{c.createdAt.slice(0, 10)}</Td>
                    <Td>
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant={c.telephonyEnabled ? 'secondary' : 'ghost'}
                          loading={telephony.isPending}
                          onClick={() =>
                            telephony.mutate({ id: c.id, enabled: !c.telephonyEnabled })
                          }
                          title={
                            c.telephonyEnabled
                              ? 'Hide the phone for this company. Their number and call history are kept.'
                              : 'Give this company the Calls tab and the browser dialer.'
                          }
                        >
                          {c.telephonyEnabled ? 'Phone on' : 'Phone off'}
                        </Button>
                        <Button size="sm" onClick={() => setExtending(c)}>
                          Extend
                        </Button>
                        {c.suspendedAt ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            loading={unsuspend.isPending}
                            onClick={() => unsuspend.mutate(c.id)}
                          >
                            Unsuspend
                          </Button>
                        ) : (
                          <Button size="sm" variant="danger" onClick={() => setSuspending(c)}>
                            Suspend
                          </Button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>

      {extending && (
        <ExtendTrialModal
          company={extending}
          onClose={() => setExtending(null)}
          onDone={() => {
            setExtending(null);
            refresh();
          }}
        />
      )}

      {suspending && (
        <SuspendModal
          company={suspending}
          onClose={() => setSuspending(null)}
          onDone={() => {
            setSuspending(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function ExtendTrialModal({
  company,
  onClose,
  onDone,
}: {
  company: AdminCompany;
  onClose: () => void;
  onDone: () => void;
}) {
  const [days, setDays] = useState(14);
  const [error, setError] = useState<string | null>(null);

  const extend = useMutation({
    mutationFn: () => api.admin.extendTrial(company.id, days),
    onSuccess: onDone,
    onError: (e) => setError((e as { message?: string }).message ?? 'Could not extend it'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Give ${company.name} more trial`}
      description="Measured from today if their trial has already run out, so the days are real days."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={extend.isPending} onClick={() => extend.mutate()}>
            Extend
          </Button>
        </>
      }
    >
      <Field label="Days" hint="1 to 365">
        <Input
          type="number"
          min={1}
          max={365}
          value={days}
          onChange={(e) => setDays(Number(e.target.value || 0))}
          autoFocus
        />
      </Field>
      <p className="mt-3 text-[13px] text-ink-3">
        This also lifts a suspension and puts them back on a trial, so anybody who was locked
        out can work again straight away.
      </p>
      {error && <p className="mt-3 text-[13px] text-danger">{error}</p>}
    </Modal>
  );
}

function SuspendModal({
  company,
  onClose,
  onDone,
}: {
  company: AdminCompany;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const suspend = useMutation({
    mutationFn: () => api.admin.suspend(company.id, reason.trim()),
    onSuccess: onDone,
    onError: (e) => setError((e as { message?: string }).message ?? 'Could not suspend them'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Suspend ${company.name}`}
      description="They keep their sign-in and can still read everything. They cannot change anything until this is lifted."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            disabled={reason.trim().length === 0}
            loading={suspend.isPending}
            onClick={() => suspend.mutate()}
          >
            Suspend
          </Button>
        </>
      }
    >
      {/*
       * The reason is required, not optional. It is read months later by
       * somebody deciding whether to lift it, and a suspension with nothing
       * attached is one nobody dares reverse.
       */}
      <Field label="Why" hint="You will read this when deciding to lift it">
        <Input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Chargeback, unpaid since August"
          autoFocus
        />
      </Field>
      {error && <p className="mt-3 text-[13px] text-danger">{error}</p>}
    </Modal>
  );
}
